import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReducer } from 'react';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import T from 'i18n';
import { Mark } from '@prizmdoc/viewer-core';
import AnnotationItem from './AnnotationItem';
import { updateAnnotationSeq } from 'features/viewing/redux/updateAnnotationSeq';
import reorder from './reorder';
import { Box, Tabs, IconButton } from '@mui/material';
import Tab from '@mui/material/Tab';
import { Close, Lock, LockOpen } from '@mui/icons-material';
import SearchWithList from 'features/common/SearchWithList';
import { selectUserAiTools, selectMatchedUrl, selectCurrentCaseId } from 'common/selectors';
import { searchReducer, initialSearchState } from './sideBarSearchReducer';
import { setSummaryOpen } from 'features/common/redux/navbarLayout';
import SearchCenter from 'features/case/SearchCenter';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  style?: any;
}

type SidebarViewerProps = {
  highlights: Array<any>;
  selectedHighlight?: any;
  scrollToHighlight: (highlight: any) => void;
  viewerControl: any;
  fileData: any;
  gridView?: boolean;
};

export default ({
  highlights,
  selectedHighlight,
  scrollToHighlight,
  viewerControl,
  fileData,
  gridView,
}: SidebarViewerProps) => {
  const dispatch = useDispatch();
  const showAiTools = useSelector(selectUserAiTools);
  const caseId = useSelector(selectCurrentCaseId);
  const currentUrl = useSelector(selectMatchedUrl);
  const [searchState, searchDispatch] = useReducer(searchReducer, initialSearchState);
  const currentSearchRequest = useRef<any>(null);
  const [sortMode, setSortMode] = useState(false);
  const [sidebarViewValue, setSidebarViewValue] = useState(0);
  const [selectedSearchIndex, setSelectedSearchIndex] = useState<number | null>(null);
  const isMainAIUrl = currentUrl.includes(`${caseId}/ai-tools`);

  // Set Ask AI tab as default if showAiTools is true and not on main AI page
  useEffect(() => {
    if (showAiTools && !isMainAIUrl) {
      setSidebarViewValue(2);
    }
  }, [showAiTools, isMainAIUrl]);
  const debouncedSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        // Check if query contains non-alphabetical characters (excluding spaces)
        const hasNonAlphabetical = /[^a-zA-Z\s]/.test(query);

        // Only search if there are at least 3 characters, unless query contains non-alphabetical characters
        // But don't process if query is empty or contains only spaces
        if (!query.trim() || (!hasNonAlphabetical && query.trim().length < 3)) {
          searchDispatch({
            type: 'UPDATE_SEARCH_STATE',
            payload: {
              results: [],
              loading: false,
            },
          });
          return;
        }

        try {
          searchDispatch({
            type: 'UPDATE_SEARCH_STATE',
            payload: {
              loading: true,
              error: null,
            },
          });
          if (currentSearchRequest.current && !currentSearchRequest.current.isComplete) {
            currentSearchRequest.current.cancel();
          }
          const searchRequest = viewerControl.search(query.trim());
          currentSearchRequest.current = searchRequest;

          searchRequest.on('PartialSearchResultsAvailable', ({ partialSearchResults }: any) => {
            if (partialSearchResults && partialSearchResults.length > 0) {
              searchDispatch({ type: 'SET_SEARCH_RESULTS', payload: partialSearchResults });
            }
          });

          searchRequest.on('SearchCompleted', ({ completedSearchResults }: any) => {
            searchDispatch({
              type: 'UPDATE_SEARCH_STATE',
              payload: {
                results: completedSearchResults || [],
                loading: false,
              },
            });
          });

          searchRequest.on('SearchCancelled', () => {
            searchDispatch({ type: 'SET_LOADING', payload: false });
          });

          searchRequest.on('SearchFailed', () => {
            searchDispatch({
              type: 'UPDATE_SEARCH_STATE',
              payload: {
                results: [],
                loading: false,
                error: 'An error occurred while searching. Please try again.',
              },
            });
          });
        } catch (err) {
          searchDispatch({
            type: 'UPDATE_SEARCH_STATE',
            payload: {
              results: [],
              loading: false,
              error: `An error occurred while searching. Please try again. ${err}`,
            },
          });
        }
      }, 500), // 500ms debounce time
    [viewerControl],
  );

  const handleClearSearch = useCallback(() => {
    searchDispatch({ type: 'CLEAR_SEARCH' });

    // Cancel any ongoing search request
    if (currentSearchRequest.current && !currentSearchRequest.current.isComplete) {
      currentSearchRequest.current.cancel();
    }
    if (viewerControl && typeof viewerControl.clearSearch === 'function') {
      try {
        viewerControl.clearSearch();
      } catch (err) {
        console.error('Error clearing search:', err);
      }
    }
  }, [viewerControl]);

  const handleSearchInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      if (newValue === '' && searchState.searchInput !== '') {
        handleClearSearch();
      } else {
        searchDispatch({ type: 'SET_SEARCH_INPUT', payload: newValue });
      }
    },
    [handleClearSearch, searchState.searchInput],
  );

  const handleResultClick = useCallback(
    (result: any, index: number) => {
      setSelectedSearchIndex(index);
      if (viewerControl && result.pageNumber) {
        try {
          // Navigate to the page containing the result
          if (typeof viewerControl.setCurrentPage === 'function') {
            viewerControl.setCurrentPage(result.pageNumber);
          } else if (typeof viewerControl.setPageNumber === 'function') {
            viewerControl.setPageNumber(result.pageNumber);
          }

          // Scroll to the result on the page
          if (typeof viewerControl.scrollTo === 'function') {
            viewerControl.scrollTo(result);
          } else if (typeof viewerControl.scrollToAsync === 'function') {
            viewerControl
              .scrollToAsync(result.location)
              .then(() => {
                // Add highlight to the search result
                if (typeof viewerControl.addMark === 'function') {
                  viewerControl.addMark({
                    type: 'highlight',
                    color: '#AFD0F9',
                    ...result,
                  });
                }
              })
              .catch((err: any) => {
                console.error('Failed to scroll to result:', err);
              });
          }
        } catch (err) {
          console.error('Error navigating to search result:', err);
        }
      }
    },
    [viewerControl],
  );

  useEffect(() => {
    // Check if query contains non-alphabetical characters (excluding spaces)
    const hasNonAlphabetical = /[^a-zA-Z\s]/.test(searchState.searchInput);
    const shouldSearch = hasNonAlphabetical || searchState.searchInput.trim().length >= 3;

    if (shouldSearch && searchState.searchInput.trim()) {
      debouncedSearch(searchState.searchInput);
    } else if (searchState.searchInput.trim().length === 0) {
      searchDispatch({
        type: 'UPDATE_SEARCH_STATE',
        payload: {
          results: [],
          loading: false,
          error: null,
        },
      });
    } else {
      searchDispatch({
        type: 'UPDATE_SEARCH_STATE',
        payload: {
          results: [],
          loading: false,
        },
      });
    }

    // Cleanup on unmount
    return () => {
      debouncedSearch.cancel();
      if (currentSearchRequest.current && !currentSearchRequest.current.isComplete) {
        currentSearchRequest.current.cancel();
      }
    };
  }, [searchState.searchInput, debouncedSearch]);

  const handleSwitchView = (event: React.SyntheticEvent, newValue: number) => {
    setSidebarViewValue(newValue);
  };

  const handleCloseSidebar = () => {
    dispatch(setSummaryOpen(false));
  };

  const handleSortModeToggle = () => {
    setSortMode((prevSortMode) => !prevSortMode);
  };

  const onlyAnnotations = highlights.filter((highlight) =>
    [Mark.Type.HighlightAnnotation, Mark.Type.RectangleAnnotation].includes(
      highlight.annotation.type,
    ),
  );

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const updatedAnnotations = reorder(
      onlyAnnotations,
      result.source.index,
      result.destination.index,
    );
    dispatch(updateAnnotationSeq({ updatedAnnotations }));
  };

  const memoizedSearchComponent = useMemo(
    () => (
      <SearchWithList
        searchInput={searchState.searchInput}
        searchResults={searchState.searchResults}
        isLoading={searchState.isLoading}
        error={searchState.searchError}
        selectedIndex={selectedSearchIndex}
        onSearchInputChange={handleSearchInputChange}
        onClearSearch={handleClearSearch}
        onResultClick={handleResultClick}
      />
    ),
    [
      searchState.searchInput,
      searchState.searchResults,
      searchState.isLoading,
      searchState.searchError,
      selectedSearchIndex,
      handleSearchInputChange,
      handleClearSearch,
      handleResultClick,
    ],
  );
  const renderSearch = () => memoizedSearchComponent;
  const renderAnnotations = () => (
    <>
      <div className="annotationsContainer" style={{ display: 'flex', paddingBottom: '14rem' }}>
        <div className="annotationsScrollbarContainer">
          {onlyAnnotations.length > 0 ? (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="annotations-list">
                {(provided) => {
                  return (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      <ul className="sidebar-view__highlights" style={{ listStyleType: 'none' }}>
                        {onlyAnnotations.map((highlight, index) => {
                          return highlight.annotation.data ? (
                            sortMode ? (
                              <Draggable
                                key={highlight.id}
                                draggableId={highlight.id}
                                index={index}
                              >
                                {(providedDrag, snapshot) => (
                                  <div
                                    ref={providedDrag.innerRef}
                                    {...providedDrag.draggableProps}
                                    {...providedDrag.dragHandleProps}
                                    style={{
                                      ...(snapshot.isDragging && {
                                        backgroundColor: 'rgba(255, 255, 255, 0.23)',
                                      }),
                                      ...providedDrag.draggableProps.style,
                                    }}
                                  >
                                    <AnnotationItem
                                      highlight={highlight}
                                      selectedHighlight={selectedHighlight}
                                      scrollToHighlightHandler={scrollToHighlight}
                                      sortMode={sortMode}
                                    />
                                  </div>
                                )}
                              </Draggable>
                            ) : (
                              <AnnotationItem
                                key={highlight.id}
                                highlight={highlight}
                                selectedHighlight={selectedHighlight}
                                scrollToHighlightHandler={scrollToHighlight}
                                sortMode={sortMode}
                              />
                            )
                          ) : null;
                        })}
                        {provided.placeholder}
                      </ul>
                    </div>
                  );
                }}
              </Droppable>
            </DragDropContext>
          ) : (
            <div className="clearAll" style={{ fontSize: '12px' }}>
              {T.translate('viewing.noAnnotationsAvailable')}
            </div>
          )}
        </div>
      </div>
    </>
  );

  const CustomTabPanel = (props: TabPanelProps) => {
    const { children, value, index, style, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        style={style}
        {...other}
      >
        {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
      </div>
    );
  };

  const a11yProps = (index: number) => {
    return {
      id: `sidebar-tab-${index}`,
      'aria-controls': `sidebar-tabpanel-${index}`,
    };
  };
  return (
    <div
      style={{
        height: '100%',
        flexDirection: 'column',
        position: 'relative',
        display: 'flex',
        flex: 1,
        overflowY: 'auto',
        paddingRight: gridView ? '0' : '1rem',
      }}
    >
      <div
        className="description-class"
        style={{
          justifyContent: 'space-between',
          flexGrow: 0,
          alignItems: 'center',
          paddingTop: '0.5rem',
        }}
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <Tabs
            value={sidebarViewValue}
            onChange={handleSwitchView}
            sx={{
              minHeight: '36px',
              flex: 1,
              '& .Mui-selected': {
                color: '#4567e5 !important',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#4567e5 !important',
              },
              '& .MuiTab-root': {
                fontSize: '12px',
              },
            }}
          >
            <Tab
              sx={{ pb: 0, pt: 0, pl: 0, minHeight: '36px' }}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {sidebarViewValue === 0 && onlyAnnotations.length > 0 && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSortModeToggle();
                      }}
                      sx={{
                        padding: '2px',
                        marginRight: '4px',
                        minWidth: 'auto',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        },
                      }}
                    >
                      {sortMode ? <LockOpen fontSize="small" /> : <Lock fontSize="small" />}
                    </IconButton>
                  )}
                  {T.translate('viewing.annotations')}
                  {onlyAnnotations.length > 0 && (
                    <Box
                      sx={{
                        ml: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#4567e5',
                        opacity: 0.75,
                        color: 'white',
                        borderRadius: '5px',
                        width: 'auto',
                        height: 'auto',
                        fontSize: '9px',
                        textAlign: 'center',
                        padding: '2px 5px',
                      }}
                    >
                      {onlyAnnotations.length}
                    </Box>
                  )}
                </Box>
              }
              {...a11yProps(1)}
            />
            <Tab label={T.translate('viewing.search')} />
            {showAiTools && !isMainAIUrl && <Tab label={T.translate('viewing.askAI')} />}
          </Tabs>
          {gridView && (
            <>
              <IconButton
                size="small"
                onClick={handleCloseSidebar}
                sx={{
                  padding: '2px',
                  minWidth: 'auto',
                  marginLeft: '8px',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      </div>
      <CustomTabPanel value={sidebarViewValue} index={0}>
        {renderAnnotations()}
      </CustomTabPanel>
      {sidebarViewValue === 1 && <div>{renderSearch()}</div>}
      {sidebarViewValue === 2 && (
        <div style={{ height: 'auto' }}>
          <SearchCenter
            fileSidebarAskAI={true}
            fileData={fileData}
            viewerControl={viewerControl}
            gridView={gridView}
          />
        </div>
      )}
    </div>
  );
};
